<script>
  import { tick, onMount } from "svelte";
  import Image from "./Image.svelte";
  import Quill from "quill";
  import ImageResize from "../../modules/ImageResize.js";

  let Font = Quill.import("formats/font");
  Font.whitelist = ["inconsolata", "roboto", "poppins", "cinzel", "mirza", "arial"];
  Quill.register(Font, true);
  Quill.register("modules/imageResize", ImageResize, true);

  export let id;
  export let value;
  export let current;
  export let label;
  export let debug = false;
  export let placeholder = "Ecrivez ici...";
  export let fa = true;
  export let icon = null;
  //export let reset = true;
  //export let required = false;
  export let margin = true;
  export let readOnly = false;

  let container;
  let editor;
  let selection = null;
  let selectionBox = null;

  $: updateBox(selection);

  async function updateBox(selection) {
    await tick();
    selectionBox = selection ? editor.getBounds(selection) : null;
  }

  function outputBox(box) {
    if (!box) return null;
    return `left: ${box.left}, top: ${box.top}, width: ${box.width}, height: ${box.height}`;
  }

  function outputSelection(selection) {
    if (!selection) return null;
    return `index: ${selection.index}, length: ${selection.length}`;
  }
  function outputValue(value) {
    if (!value) return null;
    return JSON.stringify(value.ops);
  }

  function clear() {
    editor.setContents([{ insert: "\n" }], "user");
  }

  onMount(() => {
    let options = {
      debug: debug ? "info" : "warn",
      modules: {},
      readOnly: readOnly,
    };
    if (!readOnly) {
      options.placeholder = placeholder;
      options.theme = "snow";
      options.modules.imageResize = {
        displaySize: true,
      };
      options.modules.toolbar = {
        container: [
          ["bold", "italic", "underline", "strike"], // toggled buttons
          ["blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ script: "sub" }, { script: "super" }], // superscript/subscript
          [{ indent: "-1" }, { indent: "+1" }], // outdent/indent

          [{ size: ["small", false, "large", "huge"] }], // custom dropdown
          [{ header: [1, 2, 3, 4, 5, 6, false] }],

          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ font: ["poppins", "cinzel", "roboto", "mirza", "arial"] }],
          [{ align: [] }],
          ["link"],
          ["image"],
          ["video"],
          ["clean"], // remove formatting button
        ],
      };
    }
    editor = new Quill(container, options);
    editor.setContents(value, "api");
    editor.on("selection-change", () => (selection = editor.getSelection()));
    editor.on("text-change", () => (value = editor.getContents().ops));
  });
</script>

<div class="form-group" class:in={current === id} class:filled={value} class:readOnly class:margin>
  <!-- label -->
  {#if !readOnly}
    <label class="sr-only" required for={id}>{label}</label>
  {/if}

  <!-- icon -->
  {#if icon != null && !readOnly}
    <div class="icon-left">
      {#if fa}
        <i class={icon} />
      {:else}
        <Image src={icon} />
      {/if}
    </div>
  {/if}

  <div class="container ql-snow">
    <div bind:this={container} />
    {#if debug}
      <p>
        <em>Selection: {outputSelection(selection)}</em>
        <br />
        <em>Box: {outputBox(selectionBox)}</em>
      </p>
      <p>{outputValue(value)}</p>
    {/if}
  </div>
</div>

<style>
  .container {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px;
    height: 100%;
  }

  .container :global(.ql-container) {
    font-family: "Poppins";
    font-size: 16px;
  }

  .readOnly .container {
    padding: 0;
  }

  :global(.readOnly .ql-editor) {
    padding: 0;
  }

  :global(.readOnly.form-group) {
    background: none;
  }

  .container.ql-snow :global(.ql-font span[data-value="mirza"]::before) {
    font-family: "Mirza";
    content: "Mirza";
  }
  .container.ql-snow :global(.ql-font span[data-value="roboto"]::before) {
    font-family: "Roboto";
    content: "Roboto";
  }
  .container.ql-snow :global(.ql-font span[data-value="poppins"]::before) {
    font-family: "Poppins";
    content: "Poppins";
  }
  .container.ql-snow :global(.ql-font span[data-value="cinzel"]::before) {
    font-family: "Cinzel";
    content: "Cinzel";
  }
  .container.ql-snow :global(.ql-font span[data-value="arial"]::before) {
    font-family: "Arial";
    content: "Arial";
  }

  /* Set content font-families */
  .container.ql-snow :global(.ql-font-mirza) {
    font-family: "Mirza";
  }
  .container.ql-snow :global(.ql-font-roboto) {
    font-family: "Roboto";
  }
  .container.ql-snow :global(.ql-font-arial) {
    font-family: "Arial";
  }
  .container.ql-snow :global(.ql-font-poppins) {
    font-family: "Poppins";
  }
  .container.ql-snow :global(.ql-font-cinzel) {
    font-family: "Cinzel";
  }
  .container.ql-snow :global(.ql-font-arial) {
    font-family: "Arial";
  }

  .container.ql-snow :global(.ql-tooltip) {
    z-index: 99;
  }

  .container.ql-snow :global(iframe.ql-video) {
    display: block;
    max-width: 800px;
    /*width: 100%;*/
    min-height: 360px;
    width: -webkit-fill-available;
  }

  .form-group {
    display: flex;
    position: relative;
    width: 100%;
    border-radius: 2px;
    background: #efefef;
  }
  .margin.form-group {
    margin-bottom: 15px;
  }

  .readOnly.form-group {
    background: none;
  }

  .in.form-group {
    -webkit-box-shadow: 0 2px 0 var(--primary-light-color);
    box-shadow: 0 2px 0 var(--primary-light-color);
  }
  .in.form-group .icon-left {
    color: var(--primary-color);
  }
  .form-group .icon-left {
    width: 34px;
    padding-top: 10px;
    font-size: 17px;
    font-weight: 400;
    line-height: 1;
    text-align: right;
    background-color: #eee;
    border: 0;
    color: var(--primary-light-color);
  }

  .form-group .icon-right {
    background: 0;
    cursor: pointer;
    padding: 10px 8px 0 5px;
  }

  .form-group .form-control {
    position: relative;
    width: 100%;
    margin-bottom: 0;
    background: #efefef;
  }
</style>
